import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import {
  User, AtSign, Check, X, Loader2, Save, Sparkles, Briefcase,
  Star, Mic, Trophy, XCircle, Clock, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: "day", label: "Today" },
  { key: "week", label: "7 days" },
  { key: "15day", label: "15 days" },
  { key: "month", label: "30 days" },
  { key: "all", label: "All time" },
];

const chip = (active) => ({
  padding: "0.42rem 0.9rem", borderRadius: 999, fontSize: "0.82rem", cursor: "pointer",
  border: `1.5px solid ${active ? "#6366f1" : "var(--border-main)"}`,
  background: active ? "rgba(99,102,241,0.15)" : "transparent",
  color: active ? "#6366f1" : "var(--text-secondary)",
  fontWeight: active ? 600 : 400, transition: "all 0.15s",
});

const STAT_DEFS = [
  { key: "optimizations", label: "Optimizations", icon: Sparkles, color: "#6366f1", privacy: "show_optimizations" },
  { key: "applications", label: "Applications", icon: Briefcase, color: "#3b82f6", privacy: "show_applications" },
  { key: "shortlisted", label: "Shortlisted", icon: Star, color: "#f59e0b", privacy: "show_shortlists" },
  { key: "interviews", label: "Interviews", icon: Mic, color: "#8b5cf6", privacy: "show_interviews" },
  { key: "offers", label: "Offers", icon: Trophy, color: "#10b981", privacy: "show_offers" },
  { key: "rejections", label: "Rejected", icon: XCircle, color: "#ef4444", privacy: null },
];

const PRIVACY_DEFS = [
  { key: "public_profile", label: "Public profile", help: "Let others view your profile by username" },
  { key: "show_optimizations", label: "Show optimizations" },
  { key: "show_applications", label: "Show applications" },
  { key: "show_shortlists", label: "Show shortlists" },
  { key: "show_interviews", label: "Show interviews" },
  { key: "show_offers", label: "Show offers" },
];

export default function ProfilePage() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState("week");
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ username: "", display_name: "", headline: "", bio: "" });
  const [saving, setSaving] = useState(false);
  const [unameStatus, setUnameStatus] = useState(null); // null | checking | ok | taken | invalid
  const debRef = useRef(null);

  const loadProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/social/profile/me`);
      setProfile(res.data.profile);
      setStats(res.data.stats);
      setForm({
        username: res.data.profile.username || "",
        display_name: res.data.profile.display_name || "",
        headline: res.data.profile.headline || "",
        bio: res.data.profile.bio || "",
      });
    } catch { toast.error("Failed to load profile"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const loadStats = useCallback(async (r) => {
    try {
      const res = await axios.get(`${API}/social/stats/me?range=${r}`);
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (!loading) loadStats(range); }, [range, loading, loadStats]);

  const checkUsername = (val) => {
    setForm((f) => ({ ...f, username: val }));
    setUnameStatus(null);
    if (debRef.current) clearTimeout(debRef.current);
    if (!val || val === profile?.username) return;
    setUnameStatus("checking");
    debRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/social/username-available?u=${encodeURIComponent(val)}`);
        setUnameStatus(res.data.available ? "ok" : (res.data.reason === "invalid" ? "invalid" : "taken"));
      } catch { setUnameStatus("invalid"); }
    }, 450);
  };

  const handleSave = async () => {
    if (unameStatus === "taken" || unameStatus === "invalid") {
      toast.error("Please choose a valid, available username"); return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/social/profile`, {
        username: form.username.trim().toLowerCase(),
        display_name: form.display_name,
        headline: form.headline,
        bio: form.bio,
      });
      setProfile(res.data.profile);
      setUnameStatus(null);
      await refreshUser();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally { setSaving(false); }
  };

  const togglePrivacy = async (key) => {
    const next = !profile.privacy[key];
    const optimistic = { ...profile, privacy: { ...profile.privacy, [key]: next } };
    setProfile(optimistic);
    try {
      const res = await axios.put(`${API}/social/profile`, { privacy: { [key]: next } });
      setProfile(res.data.profile);
    } catch { toast.error("Failed to update privacy"); loadProfile(); }
  };

  if (loading) {
    return <div className="analytics-loading" data-testid="profile-loading"><div className="processing-spinner" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="profile-page">
      <div className="page-header">
        <div>
          <h1 data-testid="profile-title">Your Profile</h1>
          <p style={{ color: "var(--text-secondary)" }}>Manage your public identity & track your activity.</p>
        </div>
        {profile?.username && (
          <button className="btn-secondary" data-testid="view-public-profile-btn"
            onClick={() => navigate(`/app/u/${profile.username}`)}>
            <Eye size={16} /> View public profile
          </button>
        )}
      </div>

      {/* Stats range selector */}
      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "1rem" }} data-testid="stats-range-selector">
        {RANGES.map((r) => (
          <button key={r.key} onClick={() => setRange(r.key)}
            data-testid={`range-${r.key}`} style={chip(range === r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Stat cards */}
      <div className="profile-stat-grid" data-testid="profile-stats" style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "0.9rem", marginBottom: "1.2rem",
      }}>
        {STAT_DEFS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="card" data-testid={`stat-${key}`}
            style={{ padding: "1.1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color }}>
              <Icon size={18} />
              <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{label}</span>
            </div>
            <span style={{ fontSize: "1.9rem", fontWeight: 700 }}>{stats?.[key] ?? 0}</span>
          </div>
        ))}
      </div>

      {stats?.mostly_applied_hour && (
        <div className="card" data-testid="peak-hour" style={{ padding: "0.8rem 1.1rem", marginBottom: "1.5rem", display: "flex", alignItems: "center", gap: "0.6rem", color: "var(--text-secondary)", fontSize: "0.88rem" }}>
          <Clock size={16} /> You apply most around <strong style={{ color: "var(--text-primary)" }}>{stats.mostly_applied_hour}</strong>
        </div>
      )}

      {/* Identity editor */}
      <div className="card" data-testid="identity-card" style={{ padding: "1.5rem", marginBottom: "1.2rem" }}>
        <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}><User size={18} /> Identity</h3>

        <label className="form-label" style={{ display: "block", marginTop: "1rem", marginBottom: "0.35rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Username</label>
        <div style={{ position: "relative", maxWidth: 360 }}>
          <AtSign size={16} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
          <input className="modal-input" data-testid="username-input"
            style={{ paddingLeft: 30, paddingRight: 34, width: "100%" }}
            value={form.username}
            onChange={(e) => checkUsername(e.target.value.toLowerCase())}
            placeholder="your_username" />
          <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}>
            {unameStatus === "checking" && <Loader2 size={16} className="spin" />}
            {unameStatus === "ok" && <Check size={16} color="#10b981" data-testid="username-ok" />}
            {(unameStatus === "taken" || unameStatus === "invalid") && <X size={16} color="#ef4444" data-testid="username-bad" />}
          </span>
        </div>
        {unameStatus === "taken" && <small style={{ color: "#ef4444" }}>That username is taken.</small>}
        {unameStatus === "invalid" && <small style={{ color: "#ef4444" }}>3–20 chars: lowercase letters, numbers, '.' or '_'.</small>}

        <label className="form-label" style={{ display: "block", marginTop: "1rem", marginBottom: "0.35rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Display name</label>
        <input className="modal-input" data-testid="display-name-input" style={{ maxWidth: 360, width: "100%" }}
          value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Your name" />

        <label className="form-label" style={{ display: "block", marginTop: "1rem", marginBottom: "0.35rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Headline</label>
        <input className="modal-input" data-testid="headline-input" style={{ width: "100%" }}
          value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Senior Backend Engineer · open to work" />

        <label className="form-label" style={{ display: "block", marginTop: "1rem", marginBottom: "0.35rem", fontSize: "0.82rem", color: "var(--text-secondary)" }}>Bio</label>
        <textarea className="modal-input" data-testid="bio-input" rows={3} style={{ width: "100%", resize: "vertical" }}
          value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="A short intro..." />

        <div style={{ marginTop: "1.2rem" }}>
          <button className="btn-primary" data-testid="save-profile-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save changes
          </button>
        </div>
      </div>

      {/* Privacy */}
      <div className="card" data-testid="privacy-card" style={{ padding: "1.5rem" }}>
        <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}><Eye size={18} /> Privacy</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: "0.84rem", marginTop: 0 }}>Control what others see on your public profile and leaderboards.</p>
        {PRIVACY_DEFS.map(({ key, label, help }) => (
          <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.6rem 0", borderBottom: "1px solid var(--border-main)" }}>
            <div>
              <div style={{ fontSize: "0.9rem", fontWeight: 500 }}>{label}</div>
              {help && <div style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{help}</div>}
            </div>
            <button onClick={() => togglePrivacy(key)} data-testid={`privacy-${key}`}
              className={profile.privacy[key] ? "toggle-on" : "toggle-off"}
              style={{
                width: 44, height: 24, borderRadius: 999, border: "none", cursor: "pointer", position: "relative",
                background: profile.privacy[key] ? "#6366f1" : "var(--border-main)", transition: "background 0.2s",
              }}>
              <span style={{
                position: "absolute", top: 3, left: profile.privacy[key] ? 23 : 3, width: 18, height: 18,
                borderRadius: "50%", background: "#fff", transition: "left 0.2s",
              }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
